/**
 * App shell theme (Paper light / Ink dark).
 *
 * Pure helpers only — safe for Jest without a DOM. The React context in
 * `AppThemeProvider` and the pre-paint boot script in the root layout call
 * these to resolve, normalize, and apply the theme.
 *
 * CRITICAL: this is the APP chrome theme. Reader text themes live in
 * `reader/themes.ts` and are independent.
 */

export type AppThemeId = "paper" | "ink";

export const APP_THEME_STORAGE_KEY = "lumi-app-theme";
export const DEFAULT_APP_THEME: AppThemeId = "paper";

export const APP_THEME_IDS: AppThemeId[] = ["paper", "ink"];

/** Coerce an unknown value to a valid AppThemeId, or null if not recognised. */
export function normalizeAppTheme(raw: unknown): AppThemeId | null {
  if (raw === "paper" || raw === "ink") return raw;
  if (typeof raw === "string") {
    const lower = raw.trim().toLowerCase();
    if (lower === "paper" || lower === "ink") return lower;
    // Common aliases from system prefs / older blobs.
    if (lower === "light") return "paper";
    if (lower === "dark") return "ink";
  }
  return null;
}

export interface ResolveAppThemeInput {
  /** Value from localStorage or reader_prefs.appTheme (may be missing/invalid). */
  stored?: string | null;
  /**
   * Result of `matchMedia('(prefers-color-scheme: dark)').matches`.
   * Only consulted when nothing valid is stored.
   */
  prefersDark?: boolean | null;
}

/**
 * Resolve the effective app theme.
 *
 * Priority: valid stored value → prefers-color-scheme → Paper default.
 */
export function resolveAppTheme(input: ResolveAppThemeInput = {}): AppThemeId {
  const fromStored = normalizeAppTheme(input.stored);
  if (fromStored) return fromStored;
  if (input.prefersDark === true) return "ink";
  if (input.prefersDark === false) return "paper";
  return DEFAULT_APP_THEME;
}

/** Inline boot script: set data-theme on <html> before paint (no flash). */
export const APP_THEME_BOOT_SCRIPT = `(function(){try{var k=${JSON.stringify(
  APP_THEME_STORAGE_KEY,
)};var t=localStorage.getItem(k);if(t!=="paper"&&t!=="ink"){t=(window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches)?"ink":"paper";}document.documentElement.setAttribute("data-theme",t);}catch(e){document.documentElement.setAttribute("data-theme","paper");}})();`;

/** Apply theme to the document element (client only). */
export function applyAppThemeToDocument(theme: AppThemeId): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

/** Read localStorage theme (client only); null when missing/unreadable. */
export function readStoredAppTheme(): AppThemeId | null {
  if (typeof window === "undefined") return null;
  try {
    return normalizeAppTheme(localStorage.getItem(APP_THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Persist theme to localStorage (client only). Best-effort. */
export function writeStoredAppTheme(theme: AppThemeId): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(APP_THEME_STORAGE_KEY, theme);
  } catch {
    // private mode / quota — ignore
  }
}

/**
 * Library lesson placement — captain decision: library is books-only.
 * No teaser, no lesson player, no lesson UI of any kind on /library.
 * Kept as a pure helper so tests lock the decision and future agents
 * don't re-introduce a teaser without flipping this deliberately.
 */
export function shouldShowLibraryLessonTeaser(_opts?: {
  dismissed?: boolean;
  hasBooks?: boolean;
}): boolean {
  return false;
}
